import { useEffect, useRef, useState } from "react";

export default function useInViewOnce(options = {}) {
  const {
    root = null,
    rootMargin = "0px 0px -10% 0px",
    threshold = 0.15,
    once = true,
  } = options;

  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  const [hasEntered, setHasEntered] = useState(false);

  const getThresholdNumber = (value) => {
    if (Array.isArray(value)) return value.length ? Math.min(...value) : 0;
    return typeof value === "number" ? value : 0;
  };

  const computeApproxIntersectionRatio = (element) => {
    if (!element || typeof window === "undefined") return 0;

    const rect = element.getBoundingClientRect();
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth || 0;
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;

    const width = Math.max(0, rect.width);
    const height = Math.max(0, rect.height);
    if (!width || !height || !viewportWidth || !viewportHeight) return 0;

    const visibleWidth = Math.max(0, Math.min(rect.right, viewportWidth) - Math.max(rect.left, 0));
    const visibleHeight = Math.max(0, Math.min(rect.bottom, viewportHeight) - Math.max(rect.top, 0));
    if (!visibleWidth || !visibleHeight) return 0;

    return (visibleWidth * visibleHeight) / (width * height);
  };

  useEffect(() => {
    if (once && hasEntered) return;

    const element = ref.current;
    if (!element) return;

    if (typeof window === "undefined" || !("IntersectionObserver" in window)) {
      setInView(true);
      setHasEntered(true);
      return;
    }

    const thresholdNumber = getThresholdNumber(threshold);

    const setVisible = () => {
      setInView(true);
      setHasEntered(true);
    };

    const checkNow = () => {
      const ratio = computeApproxIntersectionRatio(element);
      if (ratio >= thresholdNumber) setVisible();
    };

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry) return;

        const nextInView = Boolean(entry.isIntersecting);
        setInView(nextInView);

        if (nextInView) {
          setHasEntered(true);
          if (once) observer.disconnect();
        }
      },
      { root, rootMargin, threshold }
    );

    observer.observe(element);

    // IntersectionObserver can miss the initial state on viewport resizes
    // (e.g. toggling mobile responsive mode). This fallback check prevents
    // reveal-only content from staying invisible until the next scroll.
    const onResize = () => checkNow();
    window.addEventListener("resize", onResize, { passive: true });
    window.addEventListener("orientationchange", onResize, { passive: true });

    // Some mobile browsers can delay layout, font loading, or IO callbacks.
    // Re-check a few times after mount to avoid a permanent hidden state.
    const retryDelaysMs = [0, 50, 150, 300, 600, 1000];
    const timeoutIds = retryDelaysMs.map((delay) => window.setTimeout(checkNow, delay));

    // If the element's size changes after mount (e.g. images/fonts), check again.
    let resizeObserver;
    if (typeof window !== "undefined" && "ResizeObserver" in window) {
      resizeObserver = new ResizeObserver(() => checkNow());
      resizeObserver.observe(element);
    }

    // And one frame later for good measure.
    requestAnimationFrame(checkNow);

    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
      for (const id of timeoutIds) window.clearTimeout(id);
      if (resizeObserver) resizeObserver.disconnect();
      observer.disconnect();
    };
  }, [hasEntered, once, root, rootMargin, threshold]);

  return { ref, inView, hasEntered };
}
